import type { Package, Category } from "../components";
import { StateController } from "../state";
import { closest } from "./closest";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * null-checks window.dataLayer, assigns an empty array if null
 * then pushes the provided object to the data layer
 * @param data
 */
function pushAnalyticsData(data: object) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * Generates the base dataLayer object with common properties
 * for all data layer push events
 */
function dataLayerBase(
  event: 'load' | 'click' | 'install' | 'click-arrows',
  path: string,
  additionalData?: {}
): Object {
  return {
    event,
    path,
    display: window === window.parent ? 'Website' : 'Backoffice',
    cloud: false, // for now, need to find how to detect for later
    variant: StateController.variant,
    ...additionalData,
  };
}

/**
 * Generates the data object for a package analytics event
 * The event may be 'load' (loading the modal), 'click' (clicking an external link in the modal)
 * or 'install' (clicking the Install button in the creator card in the modal)
 * @param packageData
 * @param event
 * @param additionalData
 * @returns
 */
function analyticsPackageView(
  packageData: Package | undefined,
  event: 'load' | 'click' | 'install' = 'load',
  additionalData: Object = {}
) {
  if (!packageData) return {};

  const base = dataLayerBase(
    event,
    `/package/${
      packageData.packageIdForProvidedUmbracoVersion ?? packageData.packageId
    }`.toLowerCase()
  );

  return Object.assign(base, additionalData, {
    package: packageData.title ?? packageData.packageId,
    type: packageData.packageType,
    category: packageData.category?.name ?? 'None',
    free: packageData.licenseTypes.includes('Free'),
    hq: packageData.isHQ,
    tags: packageData.tags,
  });
}

/**
 * Push an external link click to the dataLayer
 */
export function pushExternalLinkClick(
  url: string,
  text: string,
  path: string,
  packageData?: Package
) {
  const data = {
    'click-text': text,
    'click-url': url,
    ...analyticsPackageView(packageData),
    ...dataLayerBase(
      'click',
      packageData?.packageIdForProvidedUmbracoVersion ??
        packageData?.packageId ??
        path
    ),
  };

  pushAnalyticsData(data);
}

/**
 * Push an event to datalayer when clicking the card deck arrows
 */
export function pushCarouselControlClick(element: HTMLButtonElement) {
  const data: Record<string, any> = {
    ...dataLayerBase('click-arrows', window.location.pathname, {
      action: element.dataset.action,
    }),
  };

  const deck = closest('umb-market-card-deck', element);
  if (deck && deck.hasAttribute('header')) {
    data.context = `Card deck: ${deck.getAttribute('header')}`;
  }

  const carousel = closest('umb-market-carousel', element);
  if (carousel) {
    data.context = 'Screenshot carousel';
  }

  pushAnalyticsData(data);
}

/**
 * Push a generic page view to the dataLayer
 * This does NOT track interactions with the package modal
 */
export function pushPageView(path: string, activeCategory?: Category) {
  if (path.startsWith('/package')) return;

  // TODO => we can handle the search, if we need/want to track inbound UTM etc

  // provide the category name on cat page views
  if (activeCategory) {
    pushAnalyticsData(
      dataLayerBase('load', path, { category: activeCategory?.name })
    );
    return;
  }

  pushAnalyticsData(dataLayerBase('load', path));
}

/**
 * Push a package modal interaction to the dataLayer
 */
export function pushAnalyticsPackageInteraction(
  packageData: Package,
  event: 'load' | 'click' | 'install' = 'load',
  additionalData: Object = {}
) {
  pushAnalyticsData(analyticsPackageView(packageData, event, additionalData));
}

/**
 * Determines if the clicked element is an anchor, returning the element if so
 * Exits early if the click is inside the package modal and the listener is on the document
 * If the listener is on the package modal element, returns the clicked anchor element
 */
export function getClickedLinkElement(e: Event): HTMLElement | undefined {
  const composedPath = e.composedPath();

  // can't handle package links in the generic click as we need the package for additional context
  if (
    composedPath.find(
      x => (x as HTMLElement).nodeName === 'umb-market-package-modal'
    )
  )
    return undefined;

  const element = composedPath.find(x => {
    const elm = x as HTMLElement;
    return elm.nodeName === 'A' || elm.nodeName === 'BUTTON';
  });

  return element ? (element as HTMLElement) : undefined;
}

// track external link clicks
// this works with two handlers - click handler here catches all except for LINK clicks at /package/x
// then raises the custom event to push the GTM data. Within the package modal we
// also listen for clicks, but attach the package data before emitting the link-click
// event, which is also handled below.
// Button clicks in the package modal are tracked here.
document.addEventListener('click', (e: Event) => {
  const element = getClickedLinkElement(e);
  if (!element) return;

  // track clicks on carousel controls
  if (element.nodeName === 'BUTTON' && element.hasAttribute('data-action')) {
    pushCarouselControlClick(element as HTMLButtonElement);
    return;
  }

  if (window.location.pathname.startsWith('/package')) return;

  if (element.nodeName === 'A') {
    window.dispatchEvent(
      new CustomEvent('link-click', {
        detail: {
          element,
        },
      })
    );
  }
});

window.addEventListener('link-click', (e: Event) => {
  const { element, packageData } = (e as CustomEvent).detail;

  const isExternalURL = (url?: string | null) => {
    if (!url) return false;
    const temp = document.createElement('a');
    temp.href = url;
    return temp.origin !== window.location.origin;
  };

  const href = element.getAttribute('href');
  if (isExternalURL(href)) {
    pushExternalLinkClick(
      href,
      element.innerText,
      window.location.pathname,
      packageData
    );
  }
});
